import _ from 'lodash';
import app from '$$app';
import log from '$$log';
import rights from '../../../../components/rights';
import routerHelper from '$$routerHelper';
import dnd_arrow from '../../../../svg/dnd_arrow.svg';
import getSvgIcon from '../../helpers/getSvgIcon';

class Dnd {
  #isChangingProjectsOrderInMultiview = false;

  #markerMoveHandler = null;

  #instance = null;

  isDragging = false;

  isAborting = false;

  #globalTaskId = null;

  init(instance) {
    this.#instance = instance;
    this.#instance.config.order_branch = 'marker';
    this.#instance.config.order_branch_free = true;
  }

  onRowDragStart(id, target, e) {
    const draggingTask = this.#instance.isTaskExists(id) ? this.#instance.getTask(id) : null;

    if (!draggingTask) return;
    if (!rights.project.hasRight(draggingTask.gantt_id, 'drag_and_drop')) {
      return;
    }

    if (routerHelper.isTaskSettingsRoute()) {
      return false;
    }

    if (draggingTask.$level === 0) {
      return false;
    }

    if (draggingTask.type === this.#instance.config.types.button) {
      return false;
    }

    this.isDragging = true;
    this.isAborting = false;
    userExtAnalytics.log('gantt_task_grid_drag_start');

    this.#attachMarkerToCursor();

    return true;
  }

  onBeforeRowDragMove(id, parent) {
    const draggingTask = this.#instance.isTaskExists(id) ? this.#instance.getTask(id) : null;
    const newParent = this.#instance.isTaskExists(parent) ? this.#instance.getTask(parent) : null;

    if (+id < 0) {
      return false;
    }

    if (!newParent || !draggingTask) {
      return false;
    }

    if (this.#instance.config.multiview && draggingTask.$level !== 1 && newParent.$level === 0) {
      return false;
    }

    // drag inside itself
    if (draggingTask.id === newParent.id) {
      return false;
    }

    if (newParent.type === this.#instance.config.types.button) {
      return false;
    }

    if (
      draggingTask.type === this.#instance.config.types.project
      && newParent.type === this.#instance.config.types.project
      && +draggingTask.parent !== +newParent.id
      && +draggingTask.gantt_id !== +newParent.gantt_id
    ) {
      return false;
    }

    return true;
  }

  #hasRealChildren(taskId) {
    return this.#instance.getChildren(taskId).some(
      childId => this.#instance.isTaskExists(childId)
        && this.#instance.getTask(childId).type !== this.#instance.config.types.button,
    );
  }

  #removeTargetHighlight(targetId) {
    if (this.#globalTaskId === +targetId) {
      this.#globalTaskId = null;
    }

    this.#instance.getTaskRowNode(targetId)?.classList.remove('dnd-highlight');
  }

  onRowDragMove(id, targetId, targetIndex) {
    const task = this.#instance.getTask(id);
    const target = this.#instance.getTask(targetId);

    const dndTask = document.getElementsByClassName('gantt_drag_marker')[0];
    const dndTaskMarker = document.getElementsByClassName('gantt_grid_dnd_marker')[0];

    if (!dndTask || !dndTaskMarker) {
      return;
    }

    if (dndTask.childNodes[0]?.childNodes[4]?.classList && dndTask.childNodes[0].childNodes[4].closest('.tooltip-gantt')) {
      dndTask.childNodes[0].childNodes[4].classList.remove('tooltip-gantt');
    }

    // Dnd task icon
    if (!dndTask.querySelector('.task-icon')) {
      const icon = document.createElement('div');

      if (task.type === 'project') {
        icon.innerHTML = getSvgIcon('project', 'regular', 20);
      } else {
        icon.innerHTML = getSvgIcon('file', 'regular', 20);
      }

      icon.className = 'task-icon';
      dndTask.appendChild(icon);
    }

    const markerFolder = dndTaskMarker.querySelector('.gantt_grid_dnd_marker_folder');
    const hasRealChildren = this.#hasRealChildren(target.id);
    const isExpandedParent = target.$open && (
      target.type === this.#instance.config.types.project || hasRealChildren
    );

    // Expanded parent: line marker instead of folder outline
    if (markerFolder) {
      if (isExpandedParent) {
        const linePos = this.#instance.getProjectLineMarkerPosition(target.id);

        dndTaskMarker.innerHTML = '<div class="gantt_grid_dnd_marker_line"></div>';
        dndTaskMarker.style.left = `${linePos.x}px`;
        dndTaskMarker.style.top = `${linePos.y}px`;
        dndTaskMarker.style.width = `${linePos.width}px`;
      } else if (!target.$open) {
        this.#openParent(true, target);
      }
    } else {
      this.#openParent.cancel();
    }

    // Remove previous dnd highlight
    if (this.#globalTaskId && this.#globalTaskId !== +targetId) {
      this.#removeTargetHighlight(this.#globalTaskId);
    }

    const markerFolderAfterUpdate = dndTaskMarker.querySelector('.gantt_grid_dnd_marker_folder');

    if (target.$open) {
      this.#removeTargetHighlight(target.id);

      // Nest arrow: expanded leaf task only (no row highlight)
      if (markerFolderAfterUpdate) {
        const shouldShowNestArrow = !hasRealChildren
          && target.type !== this.#instance.config.types.project;

        markerFolderAfterUpdate.querySelector('.dnd_arrow')?.remove();

        if (shouldShowNestArrow) {
          const elArrow = document.createElement('DIV');

          elArrow.innerHTML = dnd_arrow;
          elArrow.className = 'dnd_arrow';

          const grid = this.#instance.$ui.getView('grid');
          const gridLeft = grid.$grid_data.getBoundingClientRect().left;
          const column = document.querySelector('.gantt_grid_head_text');
          const columnLeft = column?.getBoundingClientRect().left ?? gridLeft;

          elArrow.style.marginLeft = `${columnLeft - gridLeft + 15}px`;
          markerFolderAfterUpdate.appendChild(elArrow);
        }
      }
    } else if (markerFolderAfterUpdate) {
      // Collapsed target: folder outline + row background
      const targetNode = this.#instance.getTaskRowNode(+targetId);

      targetNode?.classList.add('dnd-highlight');
      this.#globalTaskId = +targetId;
      markerFolderAfterUpdate.querySelector('.dnd_arrow')?.remove();
    }

    app.trigger('ganttview:contextMenu:close');
  }

  #cleanupDragUi() {
    this.#openParent.cancel();

    if (this.#globalTaskId) {
      this.#removeTargetHighlight(this.#globalTaskId);
    }

    this.#instance.$container?.querySelectorAll('.dnd-highlight').forEach(node => {
      node.classList.remove('dnd-highlight');
    });

    this.#instance.$container?.querySelectorAll('.dnd_arrow').forEach(node => {
      node.remove();
    });
  }

  onBeforeRowDragEnd(id, newParentId, tindex) {
    this.#isChangingProjectsOrderInMultiview = false;
    this.isDragging = false;

    this.#detachMarkerFromCursor();
    this.#cleanupDragUi();

    if (this.isAborting) {
      return false;
    }

    const task = this.#instance.isTaskExists(id) && this.#instance.getTask(id);
    const newParent = this.#instance.isTaskExists(newParentId) && this.#instance.getTask(newParentId);

    if (!task || !newParent) return;

    if (!this.#checkOnDepth(id)) {
      return false;
    }

    if (
      task.type === this.#instance.config.types.project
      && newParent.type === this.#instance.config.types.project
      && +task.parent !== +newParent.id
      && +task.gantt_id !== +newParent.gantt_id
    ) {
      return false;
    }

    const isMultiview = this.#instance.config.multiview;
    const isMainMultiviewParent = isMultiview && newParent.id === 1;
    const isMoveTaskBetweenProjects = !isMainMultiviewParent && task.gantt_id !== newParent.gantt_id;

    if (isMoveTaskBetweenProjects) {
      return false;
    }

    const isSamePosition = +task.parent === +newParent.id && tindex === task.$local_index;

    if (isSamePosition) {
      return false;
    }

    const isChangingProjectsOrderInMultiview = isMultiview && !newParent.gantt_id && task.$level === 1;

    if (isChangingProjectsOrderInMultiview) {
      this.#isChangingProjectsOrderInMultiview = true;
    }

    return true;
  }

  onRowDragEnd(id, targetId) {
    this.#cleanupDragUi();

    if (this.#isChangingProjectsOrderInMultiview) {
      this.#instance.callEvent('onMultiviewProjectsOrderChange', [this.#getOrder(id)]);
      this.#isChangingProjectsOrderInMultiview = false;

      return;
    }

    userExtAnalytics.log('gantt_task_grid_drop');
  }

  dragEnd() {
    const element = document.querySelector('.gantt_transparent');

    if (element) {
      this.isAborting = true;

      this.#detachMarkerFromCursor();
      this.#cleanupDragUi();

      // Trigger dhtmlx dragEnd method. Remove dhtmlx mousemove and mouseup dnd events.
      if (typeof Event === 'function') {
        element.dispatchEvent(new Event('mouseup', { bubbles: true }));
      } else {
        // IE11
        const event = document.createEvent('Event');

        event.initEvent('mouseup', true);
        element.dispatchEvent(event);
      }
    }
  }

  #getOrder(taskId) {
    return this.#instance.getSiblings(taskId).map(id => this.#instance.getTask(id).gantt_id);
  }

  #checkOnDepth(taskId) {
    let maxDepth = this.#instance.calculateTaskLevel(taskId);

    if (!maxDepth) {
      return false;
    }

    if (this.#instance.hasChild(taskId)) {
      this.#instance.eachTask(task => {
        if (this.#instance.calculateTaskLevel(task.id) > maxDepth) {
          maxDepth = this.#instance.calculateTaskLevel(task.id);
        }
      }, taskId);
    }

    return maxDepth < 14;
  }

  #attachMarkerToCursor() {
    if (this.#markerMoveHandler) {
      document.removeEventListener('mousemove', this.#markerMoveHandler);
    }

    this.#markerMoveHandler = e => {
      const dndTask = document.getElementsByClassName('gantt_drag_marker')[0];

      if (!dndTask) return;

      dndTask.style.position = 'fixed';
      dndTask.style.pointerEvents = 'none';
      dndTask.style.left = `${e.clientX + 10}px`;
      dndTask.style.top = `${e.clientY + 10}px`;
    };

    document.addEventListener('mousemove', this.#markerMoveHandler);
  }

  #detachMarkerFromCursor() {
    if (this.#markerMoveHandler) {
      document.removeEventListener('mousemove', this.#markerMoveHandler);
      this.#markerMoveHandler = null;
    }

    const dndTask = document.getElementsByClassName('gantt_drag_marker')[0];

    if (dndTask) {
      dndTask.style.position = '';
      dndTask.style.left = '';
      dndTask.style.top = '';
      dndTask.style.pointerEvents = '';
    }
  }

  #openParent = _.debounce(function(isOpen, target) {
    if (isOpen && target && !target.$open) {
      target.$open = 1;
      this.#removeTargetHighlight(target.id);

      const t = log.time('ganttRender.instance');
      this.#instance.render();
      t.end();
    }
  }, 500)
}

export default new Dnd();
